import Component from '@glimmer/component';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { on } from '@ember/modifier';
import { concat } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import * as FilePond from 'filepond';
import type { FilePondFile } from 'filepond';
import onInsert from 'scaleflow/modifiers/render-modifiers/on-insert';
import { t } from 'ember-intl';
import {
  aggregateProgress,
  formatBytes,
  progressPercent,
  type UploadProgressEntry,
} from 'scaleflow/utils/manual-repo-archives';
import RefreshCw from 'scaleflow/components/tailwind-icons/refresh-cw';
import ArrowUpTray from 'scaleflow/components/tailwind-icons/arrow-up-tray';

export interface ScansRepositoryUploadsSignature {
  Element: HTMLDivElement;
  Args: {
    Named: {
      disabled?: boolean;
      onUpload: (pondFiles: FilePondFile[]) => Promise<void> | void;
      /**
       * Live per-file upload progress. Empty or omitted renders nothing, which
       * is why the processing page — which shows per-repo status in its own
       * table — is unaffected.
       *
       * Supplied by the caller rather than tracked here because the caller owns
       * the S3 client and therefore the only source of byte counts.
       */
      progress?: UploadProgressEntry[];
      /**
       * Filenames already stored wherever this uploader writes. Any staged file
       * matching one of these will overwrite it, so the button relabels and the
       * names are listed. Empty by default, which makes the warning
       * unreachable.
       *
       * Compared case-sensitively, because S3 keys are: `API.tar.gz` and
       * `api.tar.gz` are different objects and neither collides with the other.
       */
      existingFilenames?: string[];
    };
  };
}

/** FilePond core has no file-type-validation plugin installed, so this is checked by hand. */
export function isTarGz(filename: string): boolean {
  return filename.toLowerCase().endsWith('.tar.gz');
}

/**
 * Staged filenames that already exist wherever this uploader writes, and so
 * would be overwritten.
 *
 * Compared **case-sensitively**, unlike `isTarGz` above: S3 keys are
 * case-sensitive, so `API.tar.gz` and `api.tar.gz` are genuinely different
 * objects and neither overwrites the other. Warning about them would be wrong.
 *
 * Extracted from the component so it can be tested without staging files into
 * FilePond.
 */
export function collidingFilenames(
  stagedNames: string[],
  existingNames: string[],
): string[] {
  if (!existingNames.length) return [];
  const existing = new Set(existingNames);
  return stagedNames.filter((name) => existing.has(name));
}

/**
 * Reuses FilePond the same way `Scans::ReportsUpload` does — an imperative
 * `FilePond.create` instance rather than a native drag/drop rebuild — but as a
 * `.gts` component per this app's current component convention. Progress/status
 * per file is intentionally not tracked here: the caller (the processing
 * controller) already tracks per-repo status on `scan.repositories`.
 */
export default class ScansRepositoryUploads extends Component<ScansRepositoryUploadsSignature> {
  @service declare intl: IntlService;

  pond: ReturnType<typeof FilePond.create> | null = null;

  @tracked stagedFiles: FilePondFile[] = [];
  @tracked isUploading = false;
  @tracked errorMessage: string | null = null;

  get uploadDisabled(): boolean {
    return !this.stagedFiles.length || this.isUploading;
  }

  /**
   * Staged files that would replace something already stored.
   *
   * This is the whole overwrite-confirmation mechanism: the warning below plus
   * the relabelled button *are* the confirmation, so clicking Upload is the
   * deliberate act. A modal cannot live in `onUpload` — `isUploading` is
   * already true by then, so the button would read "Uploading..." while asking
   * a question, and resolving after a cancel would still clear `stagedFiles`
   * and throw the selection away.
   */
  get collidingFiles(): string[] {
    return collidingFilenames(
      this.stagedFiles.map((file) => (file.file as File).name),
      this.args.existingFilenames ?? [],
    );
  }

  get willOverwrite(): boolean {
    return this.collidingFiles.length > 0;
  }

  get progressRows(): (UploadProgressEntry & {
    percent: number;
    loadedText: string;
    totalText: string;
  })[] {
    return (this.args.progress ?? []).map((entry) => ({
      ...entry,
      percent: progressPercent(entry),
      loadedText: formatBytes(entry.loaded),
      totalText: formatBytes(entry.total),
    }));
  }

  get overallProgress() {
    return aggregateProgress(this.args.progress ?? []);
  }

  /** More than one file in flight makes the per-file bars worth summarising. */
  get showOverallProgress(): boolean {
    return this.progressRows.length > 1;
  }

  @action
  setupPond(element: Element) {
    this.pond = FilePond.create(element as HTMLInputElement, {
      allowMultiple: true,
      disabled: this.args.disabled ?? false,
      credits: false,
      labelIdle: this.intl.t('manual-uploads.upload-idle'),
      beforeAddFile: (item: FilePondFile) => {
        if (isTarGz(item.filename)) {
          this.errorMessage = null;
          return true;
        }
        this.errorMessage = this.intl.t('manual-uploads.not-targz', {
          filename: item.filename,
        });
        return false;
      },
      onaddfile: (error: unknown, file: FilePondFile) => {
        if (!error) {
          this.stagedFiles = [...this.stagedFiles, file];
        }
      },
      onremovefile: (error: unknown, file: FilePondFile) => {
        if (!error) {
          this.stagedFiles = this.stagedFiles.filter((f) => f.id !== file.id);
        }
      },
    });
  }

  @action
  async upload() {
    if (!this.stagedFiles.length || this.isUploading || this.args.disabled) {
      return;
    }
    this.isUploading = true;
    try {
      await this.args.onUpload(this.stagedFiles);
      this.stagedFiles = [];
      this.pond?.removeFiles();
    } finally {
      this.isUploading = false;
    }
  }

  willDestroy() {
    super.willDestroy();
    this.pond?.destroy();
    this.pond = null;
  }

  <template>
    <div class={{if @disabled "opacity-50 pointer-events-none"}} ...attributes>
      <div class="px-6 py-6">
        {{! template-lint-disable require-input-label }}
        {{! `.gz` + the gzip MIME types alongside `.tar.gz`: macOS's native
        file picker filters by a file's actual last extension, so it reads
        "repo.tar.gz" as a `.gz` file and a bare `.tar.gz` filter token
        matches nothing there, silently showing every file instead. }}
        <input
          type="file"
          class="filepond"
          accept=".tar.gz,.gz,application/gzip,application/x-gzip"
          multiple
          {{onInsert this.setupPond}}
        />
        {{#if this.errorMessage}}
          <p class="mt-2 text-sm text-red-600">{{this.errorMessage}}</p>
        {{/if}}

        {{#if this.willOverwrite}}
          <div
            class="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
            data-test-overwrite-warning
          >
            <p class="text-xs text-amber-900">
              <span class="font-semibold">
                {{t "manual-uploads.replace-warning-title"}}
              </span>
              {{#each this.collidingFiles as |filename|}}
                <span
                  class="font-mono"
                  data-test-colliding-file
                >{{filename}}</span>
              {{/each}}
              &mdash;
              {{t "manual-uploads.replace-warning-body"}}
            </p>
          </div>
        {{/if}}

        {{#if this.progressRows}}
          <div class="mt-4 space-y-2" data-test-upload-progress>
            {{#each this.progressRows key="filename" as |row|}}
              <div data-test-progress-row={{row.filename}}>
                <div
                  class="flex items-baseline justify-between gap-3 text-xs text-gray-600"
                >
                  <span class="font-mono truncate">{{row.filename}}</span>
                  <span class="shrink-0 tabular-nums" data-test-progress-label>
                    {{row.loadedText}}
                    /
                    {{row.totalText}}
                    ({{row.percent}}%)
                  </span>
                </div>
                <div
                  class="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
                >
                  <div
                    class="h-full rounded-full bg-primary-600 transition-[width] duration-200"
                    style={{htmlSafe (concat "width:" row.percent "%")}}
                  ></div>
                </div>
              </div>
            {{/each}}

            {{#if this.showOverallProgress}}
              <div
                class="flex items-baseline justify-between gap-3 pt-1 text-xs font-medium text-gray-700"
                data-test-progress-total
              >
                <span>{{t "manual-uploads.progress-total"}}</span>
                <span
                  class="tabular-nums"
                >{{this.overallProgress.percent}}%</span>
              </div>
            {{/if}}
          </div>
        {{/if}}
      </div>
      <div class="px-6 py-4 flex justify-end">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          data-test-upload-button
          {{on "click" this.upload}}
          disabled={{this.uploadDisabled}}
        >
          {{#if this.isUploading}}
            <RefreshCw class="w-3.5 h-3.5 animate-spin" />
            {{t "manual-uploads.uploading"}}
          {{else}}
            <ArrowUpTray class="w-3.5 h-3.5" />
            {{#if this.willOverwrite}}
              {{t "manual-uploads.replace-button"}}
            {{else}}
              {{t "manual-uploads.upload-button"}}
            {{/if}}
            {{#if this.stagedFiles.length}}
              ({{this.stagedFiles.length}})
            {{/if}}
          {{/if}}
        </button>
      </div>
    </div>
  </template>
}
